import PropTypes from 'prop-types'
import React from 'react'

import BulkPrices from '../epages.base/BulkPrices'
import Buybox from '../../templateComponents/Buybox'
import BuyboxThemeComponent from './Buybox'
import Crossselling from '../epages.base/Crossselling'
import CustomAttributes from '../epages.base/CustomAttributes'
import EnergyLabel from '../../templateComponents/EnergyLabel'
import ImageGallery from '../epages.base/ImageGallery'
import Link from '../../templateComponents/Link'
import Percentage from '../../templateComponents/Percentage'
import PlaceholderImage from '../../PlaceholderImage'
import Price from '../../templateComponents/Price'
import ProductDescription from '../epages.base/ProductDescription'
import ProductImage from '../epages.base/ProductImage'

export default function Product({ product, shop, breadcrumb, t }) {
  return (
    <div className="product" itemScope itemType="http://schema.org/Product">
      <section className="section">
        <div className="wrapper">
          <div className="content">
            {product.slideshow ? (
              <div className="product-image-gallery-wrapper">
                <ImageGallery product={product} thumbnailListIsVertical={true} />
              </div>
            ) : (
              <div className="product-image">
                {product.image ? <ProductImage image={product.image} alt={product.name} /> : <PlaceholderImage />}
              </div>
            )}

            <div className="product-info">
              <div className="product-info-details">
                <div className="product-info-details-basic">
                  <h1 className="product-info-details-basic-title" itemProp="name">
                    {product.name}
                  </h1>
                  {product.gtin && <link itemProp={`gtin${product.gtin.length}`} content={product.gtin} />}
                  {product.basePrice && (
                    <span className="product-info-details-basic-reference">({product.basePrice.formatted})</span>
                  )}
                </div>
                {(product.price || product.lowestPrice) && (
                  <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
                    {product.price && (
                      <>
                        <link itemProp="price" content={product.price.amount} />
                        <link itemProp="priceCurrency" content={product.price.currency} />
                      </>
                    )}
                    {product.stockLevelMicrodata && (
                      <link itemProp="availability" href={`http://schema.org/${product.stockLevelMicrodata}`} />
                    )}
                    <link itemProp="itemCondition" href={`http://schema.org/${product.conditionMicrodata}`} />
                  </div>
                )}
                <div className="product-info-details-price">
                  {product.listPrice && !product.hasVariations && product.price && (
                    <div className="product-info-details-price-old">
                      <span>{product.listPrice.formatted}</span>
                      {!product.bulkPrices && (
                        <Percentage
                          oldPrice={product.listPrice.amount}
                          newPrice={product.price.amount}
                          className="product-info-details-price-percentage"
                        />
                      )}
                    </div>
                  )}
                  {!product.listPrice && product.manufacturerPrice && !product.hasVariations && product.price && (
                    <div className="product-info-details-price-old">
                      {shop.beyond && (
                        <span className="product-info-details-price-manufacturer">
                          {t('components.productComponent.priceManufacturer')}
                        </span>
                      )}
                      <span>{product.manufacturerPrice.formatted}</span>
                      {!product.bulkPrices && (
                        <Percentage
                          oldPrice={product.manufacturerPrice.amount}
                          newPrice={product.price.amount}
                          className="product-info-details-price-percentage"
                        />
                      )}
                    </div>
                  )}
                  {product.lowestPrice && (
                    <h3 className="product-info-details-price-single">
                      {t('components.productComponent.variationLowestPrice', {
                        amount: product.lowestPrice.formatted,
                      })}
                    </h3>
                  )}
                  {product.price && !product.lowestPrice && (
                    <Price
                      className={
                        product.manufacturerPrice || product.listPrice
                          ? 'product-info-details-price-new'
                          : 'product-info-details-price-single'
                      }
                      product={product}
                    />
                  )}
                  <div className="product-info-details-vat-note">
                    {t(product.vatNote, { nsSeparator: '__' })}
                    <Link className="product-info-details-shipping-link" to={shop.legalPageUrls.shipping}>
                      {' '}
                      {t('components.productComponent.plusShipping')}
                    </Link>
                  </div>
                  {shop.beyond && product.weight && (
                    <div className="product-info-details-product-weight">
                      {t('components.productComponent.productWeight', { weight: product.weight.formatted })}
                    </div>
                  )}
                </div>
                <div id="ep-app-area-product-view-price"></div>
              </div>
              {(product.energyLabel || product.productDataSheet) && (
                <div className="product-info-energy-label">
                  {product.energyLabel && (
                    <EnergyLabel
                      t={t}
                      energyLabel={product.energyLabel}
                      energyLabelSourceFile={product.energyLabelSourceFile}
                    />
                  )}
                  {product.productDataSheet && (
                    <a
                      href={product.productDataSheet}
                      target="_blank"
                      rel="noreferrer"
                      className="product-info-energy-label-product-data-sheet"
                    >
                      {t('components.productComponent.productDataSheet')}
                    </a>
                  )}
                </div>
              )}
              <Buybox product={product}>
                <BuyboxThemeComponent t={t} product={product} isBeyondShop={Boolean(shop.beyond)} />
              </Buybox>
            </div>
            <div className="product-addition-wrapper">
              <hr />
              <ProductDescription description={product.description} t={t} />
              <CustomAttributes customAttributes={product.customAttributes} t={t} />
              <BulkPrices product={product} t={t} />
            </div>
          </div>
          {product.hasCrossSelling && <Crossselling product={product} />}
        </div>
      </section>
    </div>
  )
}

Product.propTypes = {
  product: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({ href: PropTypes.string.isRequired, title: PropTypes.string.isRequired }),
  ).isRequired,
}
